import getTimesheetsStatus from '@sdk/timesheets/helpers/getTimesheetsStatus';
import TimesheetsApi from '@sdk/timesheets/api/TimesheetsApi';

import { mapShiftFeedbackFromApi } from '@web/timesheets/utils/mappers';

import { LABOR_COST_CALCULATOR_TYPE, SALES_TYPES, LARGE_CHAR } from '../../constants';
import SALES_UI_SCHEMA from '../salesUISchema';

class ReportsService {
  constructor(
    Authentication,
    PersonaCollection,
    Restangular,
    DayPartsService,
    EventCollection,
    ReportsUtils,
    LaborUISchema,
    Utils,
    TagsService,
    HttpLayer,
    sdk,
    extendUserModel,
    $timeout,
    $q
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      PersonaCollection,
      Restangular,
      DayPartsService,
      EventCollection,
      ReportsUtils,
      LaborUISchema,
      Utils,
      TagsService,
      HttpLayer,
      sdk,
      extendUserModel,
      $timeout,
      $q,
    });
    this.nonAggregatedSalesSchemaItems = SALES_UI_SCHEMA.table.items.filter(
      (schemaItem) => !schemaItem.calculateOnAggregated
    );

    this.timesheetsApi = new TimesheetsApi(HttpLayer);
  }

  async getLaborReport(from, to) {
    await this.LaborUISchema.getSchema(true);
    const response = await this.Authentication.sendRequest({
      path: `reports/labor?dates=${from}/${to}`,
    });

    return this.ReportsUtils.groupByDates(this.extendLaborModel(response.data), from, to);
  }

  async getPayrollReport(from, to) {
    let data;
    if (
      this.Authentication.user.orgSettings.laborCostCalculatorType ===
      LABOR_COST_CALCULATOR_TYPE.ICELAND
    ) {
      ({ data } = await this.Authentication.sendRequest({
        path: `reports/payroll/iceland?dates=${from}/${to}`,
      }));
      data = data.actual;
    } else {
      ({ data } = await this.Authentication.sendRequest({
        path: `reports/payroll?dates=${from}/${to}`,
      }));
    }

    data = this.extendPayrollModel(data);

    if (this.LaborUISchema.schema.payrollUISchema.table.grouped) {
      data = this.aggregatePayroll(data);
    } else {
      data = this.mapUngroupedPayroll(data);
    }

    return data;
  }

  async getSalesReport(from, to) {
    const { data } = await this.Authentication.sendRequest({
      path: `reports/sales?dates=${from}/${to}`,
    });
    return data.map(this.extendSales);
  }

  updateSalesData(data, sales) {
    const salesIndex = data.findIndex(
      (s) => sales.date === s.date && s.locationId === sales.locationId
    );
    if (salesIndex < 0) {
      return data;
    }

    const prop = sales.type === SALES_TYPES.actual ? 'actualSales' : 'projectedSales';

    data[salesIndex][prop] = sales;
    data[salesIndex] = this.extendSales(data[salesIndex]);
    return [...data];
  }

  extendSales = (originalItem) => {
    const item = { ...originalItem };
    this._extendWithEmptySales(item);

    if ('locationId' in item) {
      item.location =
        item.locationId in this.PersonaCollection.personasDict
          ? this.PersonaCollection.personasDict[item.locationId]
          : this.Restangular.restangularizeElement(null, item.locationId, 'groups');
    }

    this.nonAggregatedSalesSchemaItems.forEach((schemaItem) => {
      item[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, { data: item });
    });

    return item;
  };

  _extendWithEmptySales(sale) {
    if (!sale.projectedSales) {
      sale.projectedSales = {
        type: SALES_TYPES.override,
        amount: '',
      };
    } else if (sale.projectedSales.type === SALES_TYPES.default) {
      sale.projectedSales.type = SALES_TYPES.override;
      delete sale.projectedSales.id;
    }

    if (!sale.actualSales) {
      sale.actualSales = {
        type: SALES_TYPES.actual,
        amount: '',
      };
    }
  }

  extendLaborModel(reports) {
    const schema = this.LaborUISchema.schema.laborReportUISchema;
    const nonAggregatedSchemaItems = schema.table.items.filter(
      (schemaItem) => !schemaItem.calculateOnAggregated
    );
    return {
      cost: reports.cost.map((originalItem) => {
        const item = { ...originalItem };
        const cost = {
          date: item.date,
        };

        if ('position' in item) {
          item.position =
            item.position.id in this.PersonaCollection.personasDict
              ? this.PersonaCollection.personasDict[item.position.id]
              : this.Restangular.restangularizeElement(null, item.position, 'groups');
          cost.position = item.position;
        }

        if ('location' in item) {
          item.location =
            item.location.id in this.PersonaCollection.personasDict
              ? this.PersonaCollection.personasDict[item.location.id]
              : this.Restangular.restangularizeElement(null, item.location, 'groups');
          cost.location = item.location;
        }

        if ('user' in item) {
          item.user =
            item.user.id in this.PersonaCollection.personasDict
              ? this.PersonaCollection.personasDict[item.user.id]
              : this.Restangular.restangularizeElement(null, item.user, 'users');
          cost.user = item.user;
        }

        nonAggregatedSchemaItems.forEach((schemaItem) => {
          cost[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
            data: item,
          });
        });

        return cost;
      }),
      revenue: reports.sales
        .filter((item) => item.projectedSales || item.actualSales)
        .map((item) => ({
          ...item,
          location:
            item.locationId in this.PersonaCollection.personasDict
              ? this.PersonaCollection.personasDict[item.locationId]
              : this.Restangular.restangularizeElement(null, { id: item.locationId }, 'groups'),
          projectedSales: item.projectedSales ? Number(item.projectedSales.amount) : 0,
          actualSales: item.actualSales ? Number(item.actualSales.amount) : 0,
        })),
    };
  }

  extendPayrollModel(payrollItems) {
    return payrollItems.map((originalItem) => {
      const item = { ...originalItem };

      item.position =
        item.position.id in this.PersonaCollection.personasDict
          ? this.PersonaCollection.personasDict[item.position.id]
          : this.Restangular.restangularizeElement(null, item.position, 'groups');
      item.location =
        item.location.id in this.PersonaCollection.personasDict
          ? this.PersonaCollection.personasDict[item.location.id]
          : this.Restangular.restangularizeElement(null, item.location, 'groups');
      item.user =
        item.user.id in this.PersonaCollection.personasDict
          ? this.PersonaCollection.personasDict[item.user.id]
          : this.Restangular.restangularizeElement(null, item.user, 'users');
      this._handleNoPositionOrLocation(item);

      return item;
    });
  }

  aggregatePayroll(payrollItems) {
    if (!this.LaborUISchema.schema.payrollUISchema.table.grouped) {
      return payrollItems;
    }

    const statusSchemaField = this.LaborUISchema.schema.payrollUISchema.table.items.find(
      (schemaItem) => schemaItem.statusField
    );

    const dict = {};
    this._consolidateSalaries(payrollItems).forEach((array) => {
      const key = `${array[0].user.id}_${array[0].wage}_${array[0].position.id}_${array[0].location.id}`;

      array.forEach((item) => {
        if (!(key in dict)) {
          dict[key] = {
            shift: { ...item.shift },
            position: item.position,
            location: item.location,
            user: item.user,
            ...this.LaborUISchema.schema.payrollUISchema.table.items
              .filter((schemaItem) => !schemaItem.groupTotal && schemaItem.formula)
              .reduce((agg, schemaItem) => {
                agg[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
                  data: item,
                });

                return agg;
              }, {}),
            statuses: [{ status: item.status }],
          };
        } else {
          this.LaborUISchema.schema.payrollUISchema.table.items
            .filter(
              (schemaItem) => !schemaItem.groupTotal && schemaItem.sumUp && schemaItem.formula
            )
            .forEach((schemaItem) => {
              dict[key][schemaItem.property] +=
                this.Utils.evaluateExpression(schemaItem.formula, { data: item }) || 0;
            });

          dict[key].statuses.push({ status: item.status });
        }
      });
    });

    return Object.keys(dict).map((key) => {
      const value = dict[key];
      if (statusSchemaField) {
        value.status = getTimesheetsStatus(value.statuses);
      }

      delete value.statuses;
      value.key = key;
      return value;
    });
  }

  mapUngroupedPayroll(data) {
    return data.map((item) => {
      const res = {
        user: item.user,
        location: item.location,
        position: item.position,
      };

      this.LaborUISchema.schema.payrollUISchema.table.items.forEach((schemaItem) => {
        res[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
          data: item,
        });

        if (schemaItem.userLink) {
          res[`${schemaItem.property}-userLink`] = this.Utils.evaluateExpression(
            schemaItem.userLink,
            {
              data: item,
            }
          );
        }

        if (schemaItem.locationLink) {
          res[`${schemaItem.property}-locationLink`] = this.Utils.evaluateExpression(
            schemaItem.locationLink,
            {
              data: item,
            }
          );
        }

        if (schemaItem.positionLink) {
          res[`${schemaItem.property}-positionLink`] = this.Utils.evaluateExpression(
            schemaItem.positionLink,
            {
              data: item,
            }
          );
        }
      });

      return res;
    });
  }

  async getTimeoffReport(from, to) {
    const [reportResponse, leaveTypes] = await this.$q.all([
      this.Authentication.sendRequest({ path: `reports/leave?dates=${from}/${to}` }),
      this.EventCollection.getTimeOffTypes(),
    ]);

    const leaveTypesById = leaveTypes.reduce(
      (agg, leaveType) => ((agg[leaveType.id] = leaveType), agg),
      {}
    );

    const orderByLeaveTypes = (arr) =>
      this.Utils.sortBy(arr, [(leave) => (leave.type.name === 'Other' ? 1 : 0), 'type.name']);

    const deletedUsersDict = await this.PersonaCollection.getDeletedUsers(
      Object.keys(reportResponse.data)
    );

    return Object.entries(reportResponse.data).map(([userId, userLeaves]) => {
      const mapped = {
        user: this.PersonaCollection.personasDict[userId] || deletedUsersDict[userId],
        leaves: orderByLeaveTypes(
          Object.entries(userLeaves)
            .filter(([leaveTypeId]) => leaveTypeId !== 'remaining')
            // eslint-disable-next-line complexity
            .map(([leaveTypeId, leaveStats]) => ({
              type: leaveTypesById[leaveTypeId] || { name: 'No Type', id: 'noType' },
              pending: leaveStats.pending || 0,
              approved: leaveStats.approved || 0,
              unpaid: leaveStats.unpaid || 0,
              pendingMinutes: leaveStats.pendingMinutes || 0,
              approvedMinutes: leaveStats.approvedMinutes || 0,
              unpaidMinutes: leaveStats.unpaidMinutes || 0,
              pendingDays:
                (leaveStats.pendingMinutes || 0) /
                this.Authentication.user.orgSettings.workDayMinutes,
              approvedDays:
                (leaveStats.approvedMinutes || 0) /
                this.Authentication.user.orgSettings.workDayMinutes,
              unpaidDays:
                (leaveStats.unpaidMinutes || 0) /
                this.Authentication.user.orgSettings.workDayMinutes,
              denied: leaveStats.denied || 0,
              deniedDays:
                (leaveStats.deniedMinutes || 0) /
                this.Authentication.user.orgSettings.workDayMinutes,
              deniedMinutes: leaveStats.deniedMinutes || 0,
              ptoCost: leaveStats.ptoCost,
              remaining: leaveStats.remaining || 0,
              get requested() {
                return this.pending + this.approved;
              },
              get requestedDays() {
                return this.pendingDays + this.approvedDays;
              },
            }))
        ),
      };

      if ('remaining' in userLeaves) {
        mapped.remainingGlobal = userLeaves.remaining;
      }

      mapped.totals = {
        pending: this.Utils.sum(mapped.leaves.map((item) => item.pending)),
        approved: this.Utils.sum(mapped.leaves.map((item) => item.approved)),
        unpaid: this.Utils.sum(mapped.leaves.map((item) => item.unpaid)),
        requested: this.Utils.sum(mapped.leaves.map((item) => item.requested)),
        pendingDays: this.Utils.sum(mapped.leaves.map((item) => item.pendingDays)),
        approvedDays: this.Utils.sum(mapped.leaves.map((item) => item.approvedDays)),
        requestedDays: this.Utils.sum(mapped.leaves.map((item) => item.requestedDays)),
        unpaidDays: this.Utils.sum(mapped.leaves.map((item) => item.unpaidDays)),
        pendingMinutes: this.Utils.sum(mapped.leaves.map((item) => item.pendingMinutes)),
        approvedMinutes: this.Utils.sum(mapped.leaves.map((item) => item.approvedMinutes)),
        unpaidMinutes: this.Utils.sum(mapped.leaves.map((item) => item.unpaidMinutes)),
        denied: this.Utils.sum(mapped.leaves.map((item) => item.denied)),
        deniedDays: this.Utils.sum(mapped.leaves.map((item) => item.deniedDays)),
        deniedMinutes: this.Utils.sum(mapped.leaves.map((item) => item.deniedMinutes)),
        ptoCost: this.Utils.sum(mapped.leaves.map((item) => item.ptoCost || 0)),
        remaining: this.Utils.sum(mapped.leaves.map((item) => item.remaining)),
      };
      return mapped;
    });
  }

  async getNoShowReport(from, to) {
    const { data } = await this.Authentication.sendRequest({
      path: `reports/noshows?dates=${from}/${to}`,
    });
    const deletedUsersDict = await this.PersonaCollection.getDeletedUsers(
      Object.keys(data).map((compoundKey) => compoundKey.split('/')[0])
    );
    return Object.entries(data).map(([compoundKey, tallies]) => {
      const [userId, locationId, positionId] = compoundKey.split('/').map(Number);
      const entry = {
        user: this.PersonaCollection.personasDict[userId] || deletedUsersDict[userId],
        location: this.PersonaCollection.personasDict[locationId],
        position: this.PersonaCollection.personasDict[positionId],
        key: `${userId}_${positionId}_${locationId}`,
        ...tallies,
      };

      const schema = this.LaborUISchema.schema.noShowReportUISchema;
      schema.table.items
        .filter((schemaItem) => !schemaItem.calculateOnAggregated)
        .forEach((schemaItem) => {
          entry[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
            data: entry,
          });
        });

      return entry;
    });
  }

  _consolidateSalaries(payrollItems) {
    const consolidated = [];
    const salaries = {};

    payrollItems
      .sort((a, b) => Number(b.isSalary) - Number(a.isSalary))
      .forEach((item) => {
        const key = `${item.user.id}_${item.isOnSalary}_${item.position.id}_${item.location.id}_${item.date}`;

        if (item.isSalary) {
          salaries[key] = [item];
          consolidated.push(salaries[key]);
          return;
        }

        if (salaries[key]) {
          salaries[key].push(item);
          return;
        }

        consolidated.push([item]);
      });

    return consolidated;
  }

  _handleNoPositionOrLocation(item) {
    ['position', 'location'].forEach((group) => {
      if (item[group].id === null) {
        item[group].color = '#4C6379'; // matches no-show gray color
        item[group].name = `No ${group}`;
        item[group].sorting = LARGE_CHAR;
      }
    });
  }

  async getShiftFeedbackReport(from, to) {
    const knownTagsDict = this.TagsService.tags.reduce(
      (agg, tag) => ((agg[tag.id] = tag), agg),
      {}
    );

    const data = await this.timesheetsApi.getShiftFeedbackReport(from, to);
    const belongsToDayPart = this.sdk('process/belongsToDayPart');

    return data.map((item) =>
      mapShiftFeedbackFromApi({
        shiftFeedbackFromApi: item,
        personasDict: this.PersonaCollection.personasDict,
        knownTagsDict,
        usTimeFormat: this.Authentication.user.settings.usTimeFormat,
        dayParts: this.DayPartsService.dayParts,
        belongsToDayPart,
      })
    );
  }
}

export default ReportsService;
